import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import SelfieCheckIn from "@/components/SelfieCheckIn";
import { PlayCircle, StopCircle, Clock, CalendarPlus, Camera, MapPin, X } from "lucide-react";
import { toast } from "sonner";

export default function AttendancePage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [today, setToday] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [previewSelfie, setPreviewSelfie] = useState(null);
  const todayStr = new Date().toISOString().slice(0, 10);
  const [logForm, setLogForm] = useState({ date: todayStr, check_in: "09:30", check_out: "18:30", status: "present", note: "" });
  const [logLoading, setLogLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance", { params: { month, year } });
      setRows(data);
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  const loadToday = async () => {
    try { const { data } = await api.get("/attendance/today"); setToday(data); }
    catch (e) { console.error("attendance/today failed", e); }
  };
  useEffect(() => { load(); if (user.role === "employee") loadToday(); /* eslint-disable-next-line */ }, [month, year]);

  const checkIn = async () => {
    try { await api.post("/attendance/checkin"); toast.success("Checked in"); loadToday(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Check-in failed")); }
  };
  const checkOut = async () => {
    try { await api.post("/attendance/checkout"); toast.success("Checked out"); loadToday(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Check-out failed")); }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    setLogLoading(true);
    try {
      await api.post("/attendance/manual", logForm);
      toast.success("Attendance logged");
      setLogOpen(false);
      loadToday();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to log attendance"));
    } finally { setLogLoading(false); }
  };

  return (
    <div>
      <PageHeader
        testid="page-attendance"
        eyebrow="Timesheets"
        title="Attendance."
        description={user.role === "employee" ? "Log your day. Your HR sees this in real time." : "Every day, every employee, every hour."}
        actions={
          <div className="flex items-center gap-2">
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="filter-month"
              className="px-3 py-2 border border-neutral-300 bg-white font-mono text-sm outline-none focus:border-[#002FA7]">
              {[...Array(12)].map((_, i) => (
                <option key={i+1} value={i+1}>{String(i+1).padStart(2,"0")}</option>
              ))}
            </select>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="filter-year"
              className="px-3 py-2 border border-neutral-300 bg-white font-mono text-sm outline-none focus:border-[#002FA7]">
              {[2024,2025,2026,2027].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        }
      />

      <div className="p-8 space-y-8">
        {user.role === "employee" && (
          <div className="border border-neutral-200 bg-white p-6 flex flex-wrap items-center gap-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Today</div>
              <div className="mt-2 font-display font-black text-3xl tracking-tighter">
                {today?.check_in ? new Date(today.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                <span className="text-neutral-400 text-xl mx-2">→</span>
                {today?.check_out ? new Date(today.check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
              </div>
              <div className="mt-1 text-xs text-neutral-500 font-mono flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {today?.hours || 0} h
              </div>
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
              <button onClick={() => setSelfieOpen(true)} data-testid="btn-selfie"
                className="inline-flex items-center gap-2 bg-[#C9A961] text-neutral-950 px-4 py-2.5 font-mono text-sm hover:bg-neutral-950 hover:text-[#D4B87D] border border-[#C9A961] hover:border-neutral-950 transition-colors">
                <Camera className="w-4 h-4" /> Selfie check-in / out
              </button>
              <button onClick={checkIn} data-testid="btn-checkin" disabled={!!today?.check_in}
                className="inline-flex items-center gap-2 bg-neutral-950 text-[#D4B87D] px-4 py-2.5 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors disabled:opacity-40">
                <PlayCircle className="w-4 h-4" /> Check in
              </button>
              <button onClick={checkOut} data-testid="btn-checkout" disabled={!today?.check_in || !!today?.check_out}
                className="inline-flex items-center gap-2 border border-neutral-950 px-4 py-2.5 font-mono text-sm hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors disabled:opacity-40">
                <StopCircle className="w-4 h-4" /> Check out
              </button>
              <button onClick={() => setLogOpen(true)} data-testid="btn-log-past"
                className="inline-flex items-center gap-2 border border-[#C9A961] px-4 py-2.5 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 transition-colors">
                <CalendarPlus className="w-4 h-4" /> Log a past date
              </button>
            </div>
          </div>
        )}

        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="attendance-table">
            <thead className="bg-[#FAFAFA] border-b border-neutral-200">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="px-4 py-3">Date</th>
                {user.role !== "employee" && <th className="px-4 py-3">Employee</th>}
                <th className="px-4 py-3">Check-in</th>
                <th className="px-4 py-3">Check-out</th>
                <th className="px-4 py-3 text-right">Hours</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Proof</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-6 text-neutral-500 font-mono">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-neutral-500 font-mono">No attendance records for this month.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-[#FAFAFA] transition-colors">
                  <td className="px-4 py-3 font-mono">{r.date}</td>
                  {user.role !== "employee" && <td className="px-4 py-3 font-mono text-xs">{r.employee_id?.slice(0,8)}…</td>}
                  <td className="px-4 py-3 font-mono">{r.check_in ? new Date(r.check_in).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : "—"}</td>
                  <td className="px-4 py-3 font-mono">{r.check_out ? new Date(r.check_out).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : "—"}</td>
                  <td className="px-4 py-3 font-mono text-right">{r.hours || 0}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${r.status === "present" ? "border-[#C9A961] text-[#8C6F1E]" : "border-neutral-300 text-neutral-500"}`}>
                      {r.status || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {r.selfie_in && (
                        <button onClick={() => setPreviewSelfie({ url: r.selfie_in, label: "In", lat: r.lat_in, lng: r.lng_in, date: r.date })}
                          data-testid={`selfie-in-${r.id}`}
                          className="w-8 h-8 border border-[#C9A961] overflow-hidden hover:ring-2 hover:ring-[#C9A961] transition-all">
                          <img src={r.selfie_in} alt="in" className="w-full h-full object-cover" />
                        </button>
                      )}
                      {r.selfie_out && (
                        <button onClick={() => setPreviewSelfie({ url: r.selfie_out, label: "Out", lat: r.lat_out, lng: r.lng_out, date: r.date })}
                          data-testid={`selfie-out-${r.id}`}
                          className="w-8 h-8 border border-[#C9A961] overflow-hidden hover:ring-2 hover:ring-[#C9A961] transition-all">
                          <img src={r.selfie_out} alt="out" className="w-full h-full object-cover" />
                        </button>
                      )}
                      {(r.lat_in != null || r.lat_out != null) && (
                        <a href={`https://www.google.com/maps?q=${r.lat_in ?? r.lat_out},${r.lng_in ?? r.lng_out}`}
                          target="_blank" rel="noreferrer" title="Open map"
                          className="text-[#8C6F1E] hover:text-neutral-950 transition-colors">
                          <MapPin className="w-4 h-4" />
                        </a>
                      )}
                      {!r.selfie_in && !r.selfie_out && r.lat_in == null && <span className="text-neutral-300 font-mono text-xs">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selfieOpen && (
        <div className="fixed inset-0 bg-neutral-950/70 z-50 flex items-center justify-center p-4" onClick={() => setSelfieOpen(false)}>
          <div className="bg-white border border-[#C9A961] w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="dialog-selfie">
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">Camera + geo</div>
                <div className="font-serif font-bold text-2xl tracking-tight">Selfie attendance</div>
              </div>
              <button onClick={() => setSelfieOpen(false)} className="text-neutral-500 hover:text-neutral-950 font-mono"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <SelfieCheckIn onDone={() => { setSelfieOpen(false); loadToday(); load(); }} />
            </div>
          </div>
        </div>
      )}

      {previewSelfie && (
        <div className="fixed inset-0 bg-neutral-950/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewSelfie(null)}>
          <div className="bg-white border border-[#C9A961] w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="dialog-preview-selfie">
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">{previewSelfie.date} · Check-{previewSelfie.label.toLowerCase()}</div>
                <div className="font-serif font-bold text-2xl tracking-tight">Attendance proof</div>
              </div>
              <button onClick={() => setPreviewSelfie(null)} className="text-neutral-500 hover:text-neutral-950"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <img src={previewSelfie.url} alt="Selfie proof" className="w-full border border-neutral-200" />
              {previewSelfie.lat != null && (
                <a href={`https://www.google.com/maps?q=${previewSelfie.lat},${previewSelfie.lng}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A961] font-mono text-xs hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors">
                  <MapPin className="w-3.5 h-3.5" /> {previewSelfie.lat.toFixed(5)}, {previewSelfie.lng.toFixed(5)} · open in Google Maps
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {logOpen && (
        <div className="fixed inset-0 bg-neutral-950/60 z-50 flex items-center justify-center p-4" onClick={() => setLogOpen(false)}>
          <div className="bg-white border border-[#C9A961] w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="dialog-log-attendance">
            <div className="border-b border-neutral-200 px-6 py-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">Backfill</div>
              <div className="font-serif font-bold text-2xl tracking-tight">Log a past date</div>
            </div>
            <form onSubmit={submitManual} className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Date *</label>
                <input required type="date" max={todayStr} value={logForm.date}
                  onChange={(e) => setLogForm({...logForm, date: e.target.value})} data-testid="log-date"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Check-in (HH:MM)</label>
                <input type="time" value={logForm.check_in}
                  onChange={(e) => setLogForm({...logForm, check_in: e.target.value})} data-testid="log-checkin"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Check-out (HH:MM)</label>
                <input type="time" value={logForm.check_out}
                  onChange={(e) => setLogForm({...logForm, check_out: e.target.value})} data-testid="log-checkout"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Status</label>
                <select value={logForm.status} onChange={(e) => setLogForm({...logForm, status: e.target.value})} data-testid="log-status"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm bg-white">
                  <option value="present">Present</option>
                  <option value="wfh">Work from home</option>
                  <option value="half_day">Half day</option>
                  <option value="leave">Leave</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Note (optional)</label>
                <input value={logForm.note} onChange={(e) => setLogForm({...logForm, note: e.target.value})}
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2 text-xs text-neutral-500 font-mono">
                Existing record for this date will be overwritten. Future dates are not allowed.
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setLogOpen(false)} className="px-4 py-2.5 font-mono text-sm hover:bg-neutral-100 transition-colors">Cancel</button>
                <button type="submit" disabled={logLoading} data-testid="btn-submit-log"
                  className="px-5 py-2.5 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors disabled:opacity-50">
                  {logLoading ? "Saving…" : "Save attendance"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
